import { useState } from 'react';
import { ChevronDown, ChevronUp, ClipboardCheck, AlertCircle } from 'lucide-react';
import { Card } from '@centraoneplus/ui';
import type {
  AICompletenessConfig,
  REQUIRED_FIELD_TYPES,
} from '@/types/pipeline';

interface CompletenessConfigSectionProps {
  config: AICompletenessConfig | undefined;
  onChange: (config: AICompletenessConfig) => void;
}

const defaultCompletenessConfig: AICompletenessConfig = {
  enabled: false,
  maxClarificationAttempts: 3,
  globalRequiredFields: [],
  intentRequirements: {},
};

// Field types that can be required
const FIELD_TYPES = [
  { id: 'order_id', label: 'Order ID', description: 'Order number or reference' },
  { id: 'customer_id', label: 'Customer ID', description: 'Customer account number' },
  { id: 'email', label: 'Email Address', description: 'Valid email address' },
  { id: 'phone', label: 'Phone Number', description: 'Contact phone number' },
  { id: 'product', label: 'Product Name', description: 'Product or service name' },
  { id: 'date', label: 'Date', description: 'Relevant date (order, incident, etc.)' },
  { id: 'amount', label: 'Amount', description: 'Monetary amount' },
  { id: 'account', label: 'Account Number', description: 'Account identifier' },
];

// Intents that commonly require specific fields
const INTENT_FIELD_SUGGESTIONS: Record<string, string[]> = {
  request_refund: ['order_id', 'amount'],
  track_order: ['order_id'],
  update_account: ['account', 'email'],
  cancel_service: ['account', 'customer_id'],
  report_bug: ['product'],
  request_support: ['product'],
};

export default function CompletenessConfigSection({
  config,
  onChange,
}: CompletenessConfigSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeSection, setActiveSection] = useState<'global' | 'intents'>('global');
  const [selectedIntent, setSelectedIntent] = useState<string>('request_refund');

  const completenessConfig = config ?? defaultCompletenessConfig;

  const handleToggleEnabled = (enabled: boolean) => {
    onChange({
      ...completenessConfig,
      enabled,
    });
  };

  const handleMaxAttemptsChange = (attempts: number) => {
    onChange({
      ...completenessConfig,
      maxClarificationAttempts: attempts,
    });
  };

  const handleToggleGlobalField = (fieldId: string) => {
    const currentFields = completenessConfig.globalRequiredFields || [];
    if (currentFields.includes(fieldId)) {
      onChange({
        ...completenessConfig,
        globalRequiredFields: currentFields.filter((f) => f !== fieldId),
      });
    } else {
      onChange({
        ...completenessConfig,
        globalRequiredFields: [...currentFields, fieldId],
      });
    }
  };

  const handleToggleIntentField = (intent: string, fieldId: string) => {
    const currentRequirements = { ...completenessConfig.intentRequirements };
    const currentFields = currentRequirements[intent] || [];

    if (currentFields.includes(fieldId)) {
      currentRequirements[intent] = currentFields.filter((f) => f !== fieldId);
      if (currentRequirements[intent].length === 0) {
        delete currentRequirements[intent];
      }
    } else {
      currentRequirements[intent] = [...currentFields, fieldId];
    }

    onChange({
      ...completenessConfig,
      intentRequirements: currentRequirements,
    });
  };

  const isGlobalFieldEnabled = (fieldId: string): boolean => {
    return completenessConfig.globalRequiredFields?.includes(fieldId) ?? false;
  };

  const isIntentFieldEnabled = (intent: string, fieldId: string): boolean => {
    return completenessConfig.intentRequirements?.[intent]?.includes(fieldId) ?? false;
  };

  const intentsWithRequirements = Object.keys(completenessConfig.intentRequirements || {});

  // Available intents for configuration
  const AVAILABLE_INTENTS = [
    { id: 'request_refund', label: 'Request Refund' },
    { id: 'track_order', label: 'Track Order' },
    { id: 'update_account', label: 'Update Account' },
    { id: 'cancel_service', label: 'Cancel Service' },
    { id: 'report_bug', label: 'Report Bug' },
    { id: 'request_support', label: 'Request Support' },
    { id: 'request_feature', label: 'Request Feature' },
    { id: 'complaint', label: 'Complaint' },
    { id: 'get_information', label: 'Get Information' },
  ];

  return (
    <Card className="p-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <ClipboardCheck className="h-5 w-5 text-orange-600" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Completeness Validation</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Define required fields for AI resolution
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-6 space-y-6">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between pb-4 border-b">
            <div>
              <p className="font-medium text-gray-700 dark:text-gray-200">Enable Completeness Validation</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Check if emails have required information before AI resolution
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={completenessConfig.enabled}
                onChange={(e) => handleToggleEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 dark:bg-gray-700"></div>
            </label>
          </div>

          {completenessConfig.enabled && (
            <>
              {/* Max Clarification Attempts */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Max Clarification Attempts
                </label>
                <input
                  type="number"
                  value={completenessConfig.maxClarificationAttempts}
                  onChange={(e) => handleMaxAttemptsChange(parseInt(e.target.value) || 3)}
                  min={1}
                  max={5}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                />
                <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                  Number of times AI will ask for missing information before handoff
                </p>
              </div>

              {/* Section Tabs */}
              <div className="flex gap-2 border-b">
                <button
                  onClick={() => setActiveSection('global')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    activeSection === 'global'
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                  }`}
                >
                  Global ({completenessConfig.globalRequiredFields?.length || 0})
                </button>
                <button
                  onClick={() => setActiveSection('intents')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    activeSection === 'intents'
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                  }`}
                >
                  Per Intent ({intentsWithRequirements.length})
                </button>
              </div>

              {/* Global Required Fields */}
              {activeSection === 'global' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Fields required for ALL tickets in this pipeline, regardless of intent or category.
                  </p>

                  <div className="grid grid-cols-2 gap-3">
                    {FIELD_TYPES.map((field) => {
                      const isEnabled = isGlobalFieldEnabled(field.id);

                      return (
                        <div
                          key={field.id}
                          onClick={() => handleToggleGlobalField(field.id)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                            isEnabled
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 bg-white hover:border-gray-300 dark:bg-gray-800 dark:border-gray-700'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isEnabled}
                              onChange={() => {}}
                              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-700"
                            />
                            <div>
                              <p
                                className={`text-sm font-medium ${
                                  isEnabled ? 'text-primary-900' : 'text-gray-900 dark:text-gray-100'
                                }`}
                              >
                                {field.label}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{field.description}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Per-Intent Required Fields */}
              {activeSection === 'intents' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Additional fields required based on detected intent. These are added to global requirements.
                  </p>

                  <div className="flex gap-4">
                    {/* Intent Selector */}
                    <div className="w-1/3">
                      <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-200">
                        Select Intent
                      </label>
                      <div className="space-y-1">
                        {AVAILABLE_INTENTS.map((intent) => {
                          const hasRequirements =
                            (completenessConfig.intentRequirements?.[intent.id]?.length || 0) > 0;

                          return (
                            <button
                              key={intent.id}
                              onClick={() => setSelectedIntent(intent.id)}
                              className={`w-full text-left px-3 py-2 text-sm rounded-lg ${
                                selectedIntent === intent.id
                                  ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/40 dark:text-primary-200'
                                  : 'hover:bg-gray-100 text-gray-700 dark:text-gray-200 dark:hover:bg-gray-700'
                              }`}
                            >
                              <span>{intent.label}</span>
                              {hasRequirements && (
                                <span className="ml-2 text-xs text-primary-600">
                                  ({completenessConfig.intentRequirements?.[intent.id]?.length})
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Field Selection */}
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-200">
                        Required Fields for "{AVAILABLE_INTENTS.find((i) => i.id === selectedIntent)?.label}"
                      </label>

                      {/* Suggestion Badge */}
                      {INTENT_FIELD_SUGGESTIONS[selectedIntent] && (
                        <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 rounded-lg">
                          <AlertCircle className="h-4 w-4 text-blue-500" />
                          <span className="text-xs text-blue-700">
                            Suggested:{' '}
                            {INTENT_FIELD_SUGGESTIONS[selectedIntent]
                              .map((f) => FIELD_TYPES.find((ft) => ft.id === f)?.label)
                              .join(', ')}
                          </span>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        {FIELD_TYPES.map((field) => {
                          const isEnabled = isIntentFieldEnabled(selectedIntent, field.id);

                          return (
                            <div
                              key={field.id}
                              onClick={() => handleToggleIntentField(selectedIntent, field.id)}
                              className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                isEnabled
                                  ? 'border-primary-500 bg-primary-50'
                                  : 'border-gray-200 bg-white hover:border-gray-300 dark:bg-gray-800 dark:border-gray-700'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={isEnabled}
                                  onChange={() => {}}
                                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-700"
                                />
                                <span className="text-sm">{field.label}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </>
          )}

          {/* Help Text */}
          <div className="bg-orange-50 border border-orange-100 rounded-lg p-4 mt-4">
            <h4 className="text-sm font-medium text-orange-900 mb-2">How Completeness Validation Works</h4>
            <ul className="text-xs text-orange-800 space-y-1">
              <li>
                <strong>Field Detection:</strong> AI extracts fields from email content automatically
              </li>
              <li>
                <strong>Requirement Levels:</strong> Global + Intent = Total required fields
              </li>
              <li>
                <strong>Clarification:</strong> If fields are missing, AI asks customer for information
              </li>
              <li>
                <strong>Handoff:</strong> After max attempts, ticket is handed off to human agent
              </li>
            </ul>
          </div>
        </div>
      )}
    </Card>
  );
}
